/* PayCasualModal — quick till-side casual wage payment.
 *
 * Stripped-down version of /casual-wages aimed at cashiers: pick worker,
 * hours+rate OR lump sum, pay → till deducts cash, slip auto-prints.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Banknote, Loader2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => "R " + Math.round(Number(n || 0)).toLocaleString("en-ZA");

const todaySAST = () => {
  const d = new Date();
  return new Date(d.getTime() + (2 * 60 - d.getTimezoneOffset()) * 60000).toISOString().slice(0, 10);
};

export default function PayCasualModal({ open, onClose }) {
  const { user } = useAuth();
  const [allActive, setAllActive] = useState([]);   // every active employee
  const [companyName, setCompanyName] = useState("");
  const [empId, setEmpId] = useState("");
  const [mode, setMode] = useState("hourly");
  const [hours, setHours] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [lumpSum, setLumpSum] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Robust "is casual" check — case/space/format insensitive.
  // Treats either `employment_type` or `salary_type` containing "casual" as
  // a casual. The Employees form writes the classification to
  // `employment_type` (with `salary_type` then capturing whether they're
  // hourly/monthly/etc), so we must check both to detect every record.
  const isCasual = (e) => {
    const t = `${e?.employment_type || ""} ${e?.salary_type || ""}`.toLowerCase();
    return t.includes("casual");
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [emp, settings] = await Promise.all([
          axios.get(`${API}/api/employees`),
          axios.get(`${API}/api/settings`).catch(() => ({ data: {} })),
        ]);
        const active = (emp.data || []).filter(e => e.is_active);
        // Sort casuals first so they're top of the list
        active.sort((a, b) => {
          const ac = isCasual(a) ? 0 : 1;
          const bc = isCasual(b) ? 0 : 1;
          if (ac !== bc) return ac - bc;
          return String(a.first_name || "").localeCompare(String(b.first_name || ""));
        });
        setAllActive(active);
        setCompanyName(settings.data?.company_name || "");
        // Default to the first casual; if none, the first active employee
        const firstCasual = active.find(isCasual);
        const seed = firstCasual || active[0];
        if (seed) {
          setEmpId(seed.id);
          if (seed.basic_salary) setHourlyRate(String(seed.basic_salary));
        }
      } catch {
        toast.error("Failed to load staff");
      }
    })();
  }, [open]);

  // When user changes employee, refresh the default rate
  useEffect(() => {
    const e = allActive.find(c => c.id === empId);
    if (e?.basic_salary) setHourlyRate(String(e.basic_salary));
  }, [empId, allActive]);

  const amount = useMemo(() => {
    if (mode === "lump") return Math.round(Number(lumpSum) || 0);
    return Math.round((Number(hours) || 0) * (Number(hourlyRate) || 0));
  }, [mode, hours, hourlyRate, lumpSum]);

  const reset = () => {
    setHours(""); setLumpSum(""); setNote("");
  };

  const pay = async () => {
    if (!empId) { toast.error("Pick a worker"); return; }
    if (amount <= 0) { toast.error("Enter hours/amount"); return; }
    setBusy(true);
    try {
      const body = {
        employee_id: empId,
        date: todaySAST(),
        note,
        paid_by_user_id: user?.id,
        paid_by_user_name: user?.display_name || user?.username || "",
      };
      if (mode === "lump") body.lump_sum = Number(lumpSum);
      else { body.hours = Number(hours); body.hourly_rate = Number(hourlyRate); }
      const res = await saveWithRetry(() => axios.post(`${API}/api/payroll/casual/pay`, body));
      toast.success(`Paid ${fmt(res.data.amount)} to ${res.data.employee_name}`);
      printSlip(res.data, companyName);
      reset();
      onClose && onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Pay failed"));
    } finally {
      setBusy(false);
    }
  };

  const selected = allActive.find(c => c.id === empId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-md" data-testid="pay-casual-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <Banknote className="h-5 w-5" />
            Pay Casual — from Till
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {allActive.length === 0 ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              No active staff. Add a worker in Employees first.
            </div>
          ) : (
            <>
              <div>
                <Label className="text-sm">Worker</Label>
                <Select value={empId} onValueChange={setEmpId}>
                  <SelectTrigger className="h-11 text-base" data-testid="pc-employee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allActive.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} {isCasual(c) ? "· casual" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selected && !isCasual(selected) && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    ⚠ {selected.first_name} {selected.last_name} is not marked as a casual.
                    The payment will still be recorded, but month-end roll-up will not net them off automatically.
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={mode === "hourly" ? "default" : "outline"}
                  onClick={() => setMode("hourly")}
                  className={`flex-1 ${mode === "hourly" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                  data-testid="pc-mode-hourly"
                >
                  Hours × Rate
                </Button>
                <Button
                  size="sm"
                  variant={mode === "lump" ? "default" : "outline"}
                  onClick={() => setMode("lump")}
                  className={`flex-1 ${mode === "lump" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                  data-testid="pc-mode-lump"
                >
                  Lump sum
                </Button>
              </div>

              {mode === "hourly" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Hours</Label>
                    <Input
                      type="number"
                      step="0.25"
                      inputMode="decimal"
                      value={hours}
                      onChange={e => setHours(e.target.value)}
                      placeholder="8"
                      className="h-12 text-xl text-right"
                      autoFocus
                      data-testid="pc-hours"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Rate /hr</Label>
                    <Input
                      type="number"
                      step="0.5"
                      inputMode="decimal"
                      value={hourlyRate}
                      onChange={e => setHourlyRate(e.target.value)}
                      placeholder={selected?.basic_salary || "25"}
                      className="h-12 text-xl text-right"
                      data-testid="pc-rate"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-sm">Amount to pay (R)</Label>
                  <Input
                    type="number"
                    step="1"
                    inputMode="decimal"
                    value={lumpSum}
                    onChange={e => setLumpSum(e.target.value)}
                    placeholder="e.g. 200"
                    className="h-12 text-xl text-right"
                    autoFocus
                    data-testid="pc-lump"
                  />
                </div>
              )}

              <div>
                <Label className="text-sm">Note (optional)</Label>
                <Input value={note} onChange={e => setNote(e.target.value)} placeholder="What did they do?" />
              </div>

              <div className="bg-emerald-50 border-2 border-emerald-300 rounded p-3 flex items-center justify-between">
                <span className="text-emerald-900">Cash from till</span>
                <span className="text-3xl font-bold text-emerald-700" data-testid="pc-amount">{fmt(amount)}</span>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          {allActive.length > 0 && (
            <Button
              onClick={pay}
              disabled={busy || amount <= 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="pc-pay-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Banknote className="h-4 w-4 mr-1" />}
              Pay & print slip
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Same 80mm thermal layout used on /casual-wages, kept inline so the
 * till modal is fully self-contained. */
function printSlip(w, companyName = "") {
  const win = window.open("", "_blank", "width=400,height=600");
  if (!win) { toast.error("Pop-up blocked — allow pop-ups for this site"); return; }
  const safe = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  win.document.write(`
<!DOCTYPE html><html><head><title>Wage Slip ${safe(w.wage_slip_number)}</title>
<style>
@page { size: 80mm auto; margin: 4mm; }
body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; }
h1 { font-size: 14px; margin: 0 0 4px 0; text-align: center; }
hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
.line { display: flex; justify-content: space-between; }
.lbl { font-weight: bold; }
.big { font-size: 18px; font-weight: bold; text-align: center; padding: 4px; border: 2px solid #000; margin: 6px 0; }
.sig { margin-top: 12px; }
.sig-line { border-bottom: 1px solid #000; height: 16px; }
.sub { font-size: 9px; color: #555; text-align: center; }
</style></head><body>
<h1>${safe(companyName || "WAGE SLIP")}</h1>
<div class="sub">CASUAL WAGE — DAILY PAYMENT</div>
<hr/>
<div class="line"><span class="lbl">Slip #</span><span>${safe(w.wage_slip_number)}</span></div>
<div class="line"><span class="lbl">Date</span><span>${safe(w.date)}</span></div>
<div class="line"><span class="lbl">Petty cash</span><span>${safe(w.petty_cash_number || "")}</span></div>
<hr/>
<div class="line"><span class="lbl">Worker</span><span>${safe(w.employee_name)}</span></div>
${w.department ? `<div class="line"><span class="lbl">Dept</span><span>${safe(w.department)}</span></div>` : ""}
<hr/>
${w.wage_basis === "hourly" ? `
  <div class="line"><span class="lbl">Hours</span><span>${Number(w.hours).toFixed(2)}</span></div>
  <div class="line"><span class="lbl">Rate /hr</span><span>R ${Number(w.hourly_rate).toFixed(2)}</span></div>
` : `
  <div class="line"><span class="lbl">Lump sum</span><span>R ${Number(w.lump_sum).toFixed(2)}</span></div>
`}
${w.note ? `<div class="line" style="margin-top:4px"><span class="lbl">Note</span><span>${safe(w.note)}</span></div>` : ""}
<div class="big">PAID  R ${Math.round(Number(w.amount)).toLocaleString("en-ZA")}</div>
<div class="sig"><div class="sub">Worker signature (acknowledges receipt of cash)</div><div class="sig-line"></div></div>
<div class="sig"><div class="sub">Paid by</div><div class="sig-line">${safe(w.paid_by_user_name || "")}</div></div>
<hr/>
<div class="sub">Printed ${new Date().toLocaleString("en-ZA")}</div>
<script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };</script>
</body></html>`);
  win.document.close();
}
